import { ApolloError } from "@apollo/client";
import { createContext } from "react";
import {
  EnumTagGroupKey,
  EnumTagGroupType,
  PersonDetailsQuery,
  TagByIdQuery,
  TagGroup,
  TagManyWithCountsQuery,
} from "gql/generated";

export interface TagLike {
  id: string;
  value: string; // Needed for the Combobox
  label: string;
  selected: boolean;
}

export type SelectableTagWithCount = TagLike &
  TagManyWithCountsQuery["tagMany"][0];

export type TagGroupWithSelectableTags = {
  tagGroup: TagGroup;
  tags: SelectableTagWithCount[];
};

export type PeopleTagsContextValues = {
  systemRoles: TagLike[];
  selectedSystemRoleIds: string[];
  resetSelectedSystemRoleIds: () => void;
  tagGroup: TagGroup | undefined;
  selectedOrganizationTagIds: string[];
  resetSelectedOrganizationTagIds: () => void;
  /** "Test Users (N)" filter chip; null when the org has no Test Data users. */
  testUsersTag: TagLike | null;
  toggleTestUsers: (selected: boolean) => void;
  membershipTierTags: SelectableTagWithCount[];
  organizationTagsLoading: boolean;
  organizationTagsError: ApolloError | undefined;
  createTagLoading: boolean;
  createTagError: ApolloError | undefined;
  updatingTagsForPersonLoading: boolean;
  updatingTagsForPersonError: ApolloError | undefined;
  allTagGroupsWithTags: TagGroupWithSelectableTags[];
  editingTag: SelectableTagWithCount | null;
  setEditingTag: (tag: SelectableTagWithCount | null) => void;
  flyoutCategoryOptions: {
    value: string;
    label: string;
    key: EnumTagGroupKey;
    type: EnumTagGroupType;
  }[];
  onTagUpdated: (tag: TagByIdQuery["tagById"]) => void;
  onTagDeleted: (tag: TagByIdQuery["tagById"]) => void;
  onTagSplitCompleted: (
    updatedTag: TagByIdQuery["tagById"],
    newTags: TagByIdQuery["tagById"][],
  ) => void;
  onTagMergeCompleted: (tag: TagByIdQuery["tagById"]) => void;
  /** Whether people can be tagged in this view. */
  canAssignTags: boolean;
  /** Whether the tag list itself (create, rename, delete) can be edited here. */
  canManageTags: boolean;
  createTag: (label: string) => Promise<SelectableTagWithCount | null>;
  updateTagsForPerson: (
    person: PersonDetailsQuery["personDetails"],
    tagIds: string[],
  ) => Promise<string[] | null>;
  toggleSystemRole: (systemRole: TagLike, selected: boolean) => void;
  toggleOrganizationTag: (
    tag: SelectableTagWithCount,
    selected: boolean,
  ) => void;
};

export const PeopleTagsContext = createContext<PeopleTagsContextValues | null>(
  null,
);
